import { css } from 'emotion';
import theme from 'site-react/theme';

/*
 * A brief explanation, because this is a little bit counter-intuitive.
 *
 * Our buttons normally display with a height of 54px - this is a combination
 * of our text size, padding, and borders.
 *
 * This isn't technically divisible by 4, so it's slightly off our spacing
 * system. It's done special here, so we can have perfectly round buttons (when
 * a button only has one line of text), but this isn't a pattern we should use
 * widely.
 */
const expectedButtonHeight = theme.spacing.base * 13.5;

const brandPrimaryGlow = `0 0 0 0 rgba(${theme.colors.brandPrimaryRgb}, 0.4)`;
const brandPrimaryGlowActive = `0 20px 40px 0 rgba(${theme.colors.brandPrimaryRgb}, 0.4)`;

/*
 * .sb__ classes are used to simulate various states in our Storybook stories.
 * They should never be used in product.
 */

/* Disabled styles - shared across all the different button styles */

const disabledPrimaryStyle = css`
  background-color: ${theme.colors.neutral100};
  border-color: ${theme.colors.neutral100};
  color: ${theme.colors.neutral300};
  cursor: default;

  :focus,
  &.sb__focus,
  :hover,
  &.sb__hover,
  :active,
  &.sb__active {
    box-shadow: none;
  }
`;

const disabledSecondaryStyle = css`
  background-color: transparent;
  border-color: ${theme.colors.neutral100};
  color: ${theme.colors.neutral300};
  cursor: default;
`;

/* Basic button styles */

export const buttonStyle = css`
  ${theme.largeLabel}
  border-style: solid;
  border-width: 2px;
  border-radius: ${expectedButtonHeight / 2}px;
  color: inherit;
  cursor: pointer;
  display: inline-block; /* Always behave like a <button>, even for <a> */
  position: relative;
  text-align: center;
  text-transform: uppercase;
  transition-duration: ${theme.animation.timings.short};
  transition-property: background-color, border-color, box-shadow, color;
  transition-timing-function: linear;
  vertical-align: middle;

  /*
   * The -3 here is to optically center the uppercase text inside the button.
   * Because uppercase characters have no descenders, we need to account for the
   * empty space this leaves behind.
   *
   * Total button height is spacing.lg * 2.25 = 54px
   */
  padding: ${theme.spacing.md}px ${theme.spacing.lg}px ${theme.spacing.md - 3}px;

  :focus,
  &.sb__focus,
  :hover,
  &.sb__hover,
  :active,
  &.sb__active {
    /* Reset default <a> styles */
    text-decoration: none;
    transition-duration: ${theme.animation.timings.short};
    transition-property: background-color, border-color, box-shadow, color;
    transition-timing-function: linear;
  }
`;

/* `styleType`s */

export const primaryButtonStyle = css`
  background-color: ${theme.colors.brandPrimary};
  border-color: ${theme.colors.brandPrimary};
  box-shadow: ${brandPrimaryGlow};
  color: ${theme.colors.white};

  :focus,
  &.sb__focus {
    outline: none;
  }

  :hover,
  &.sb__hover,
  :focus,
  &.sb__focus {
    background-color: ${theme.colors.brandPrimaryHover};
    border-color: ${theme.colors.brandPrimaryHover};
    box-shadow: ${brandPrimaryGlowActive};
    /* Reset default <a> styles */
    color: ${theme.colors.white};
  }

  :active,
  &.sb__active {
    background-color: ${theme.colors.brandPrimaryActive};
    border-color: ${theme.colors.brandPrimaryActive};
    box-shadow: ${brandPrimaryGlow};
    /* Reset default <a> styles */
    color: ${theme.colors.white};
  }

  :disabled {
    ${disabledPrimaryStyle};
  }
`;

export const secondaryButtonStyle = css`
  border-color: ${theme.colors.neutral900};
  background-color: ${theme.colors.transparent};

  :focus,
  &.sb__focus,
  :hover,
  &.sb__hover,
  :active,
  &.sb__active {
    /* Reset default <a> styles */
    background-color: ${theme.colors.neutral900};
    color: ${theme.colors.white};
  }

  :disabled {
    ${disabledSecondaryStyle};
  }
`;

export const secondaryButtonStyleWhite = css`
  border-color: ${theme.colors.white};
  background-color: ${theme.colors.transparent};
  color: ${theme.colors.white};

  :focus,
  &.sb__focus,
  :hover,
  &.sb__hover,
  :active,
  &.sb__active {
    /* Reset default <a> styles */
    background-color: ${theme.colors.white};
    color: ${theme.colors.neutral900};
  }

  :disabled {
    ${disabledSecondaryStyle};
  }
`;

/* Modifiers for the small breakpoint */

export const smallFullWidthStyle = css`
  /*
  * Because of the way Grid shrink wraps elements, having width of 100% means it never actually
  * goes full screen, it 'expands' to 100% of it's shrink wrapped container, which is just the
  * same size as it is on desktop. Doing this explicitly says:
  * "At mobile, be the full width minus the gutters that exist on all mobile viewports"
  * This means we can center items on tablet view without having to create wrapper containers
  * and our Grid can work as intended.
  */
  width: calc(100vw - ${theme.spacing.xl}px);

  @media (min-width: ${theme.breakpoints.md}px) {
    width: auto;
  }
`;

export const smallIconOnlyStyle = css`
  background-color: ${theme.colors.transparent};
  border-width: 0;
  padding: ${theme.spacing.sm}px; /* Increase the touch target a smidge. */

  @media (min-width: ${theme.breakpoints.md}px) {
    border-width: 2px;
    padding: ${theme.spacing.md}px ${theme.spacing.xxl}px;
  }
`;

/* Other modifiers */

export const blockStyle = css`
  width: 100%;

  @media (min-width: ${theme.breakpoints.md}px) {
    width: 100%;
  }
`;

const compactTopPadding = theme.spacing.sm;
/*
 * The -2 here is to optically center the uppercase text inside the button.
 * Because uppercase characters have no descenders, we need to account for the
 * empty space this leaves behind.
 */
const compactBottomPadding = theme.spacing.sm - 2;
/* Exact height of a single line of text */
const compactContentHeight = 21;
const compactBorderWidth = 2;

const expectedCompactHeight =
  compactTopPadding +
  compactContentHeight +
  compactBottomPadding +
  compactBorderWidth * 2;

export const compactStyle = css`
  border-radius: ${expectedCompactHeight / 2}px;
  padding: ${compactTopPadding}px ${theme.spacing.md}px
    ${compactBottomPadding}px;
`;

/* Descendent styles */

/**
 * We want to hide everything inside the button - icons and labels - but we want
 * to keep the size consistent. To accomplish that, we set visibility: hidden;
 * whenever the spinner is visible. This prevents the size of the button from
 * jumping around as different elements become visible/hidden.
 */
export const hiddenForSpinnerStyle = css`
  visibility: hidden;
`;

export const iconStyle = css`
  font-size: ${theme.font.size.sm};
  line-height: ${theme.lineHeight.reset};
  margin-right: ${theme.spacing.sm}px;
  vertical-align: inherit;
`;

export const iconStyleSmallIconOnly = css`
  margin-right: 0;

  @media (min-width: ${theme.breakpoints.md}px) {
    margin-right: ${theme.spacing.sm}px;
  }
`;

export const labelStyle = css`
  color: inherit;
  margin: 0;
  padding: 0;
`;

export const spinnerWrapperStyle = css`
  align-items: center;
  bottom: 0;
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
`;
